/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";

import type { TicketData } from "@spwn/types/firebase/database";
import type { TicketConfig } from "@spwn/types/firebase/firestore";

type Props = {
  chosenTicket: TicketData;
  ticketConfig: TicketConfig;
  quantity: number;
  changeCountOfTicket: (
    ticketId: string,
    incrementOrDecrement: "increment" | "decrement"
  ) => void;
  overrideMaxPurchaseLimit: number | undefined;
};

const PlusIcon = () => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M11 16H21" stroke="#333333" strokeWidth="1.5" />
      <path d="M16 11L16 21" stroke="#333333" strokeWidth="1.5" />
    </svg>
  );
};

const MinusIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
    >
      <path d="M11 16H21" stroke="#333333" strokeWidth="1.5" />
    </svg>
  );
};

export const TicketQuantitySelector: React.FC<Props> = ({
  chosenTicket,
  ticketConfig,
  quantity,
  overrideMaxPurchaseLimit,
  changeCountOfTicket,
}) => {
  const { t } = useI18n();

  /**
   * legacy/packages/portal/src/containers/event/eventTicket/EventTicketSelectNum.tsx:L72での
   * 最大購入枚数の算出ロジックと同様
   */
  const PURCHASE_MAX_LIMIT = 99;
  const maxPurchase = overrideMaxPurchaseLimit
    ? overrideMaxPurchaseLimit
    : Math.min(
        chosenTicket.purchaseLimit,
        ticketConfig.config?.maxTicketPurchase ?? PURCHASE_MAX_LIMIT,
        PURCHASE_MAX_LIMIT
      );

  const handleIncrement = () => {
    if (quantity < maxPurchase) {
      changeCountOfTicket(chosenTicket._id, "increment");
    }
  };

  const handleDecrement = () => {
    if (quantity > 0) {
      changeCountOfTicket(chosenTicket._id, "decrement");
    }
  };

  return (
    <div>
      <div css={styles.label}>{t("event.ticket.headline.quantity")}</div>
      <div css={styles.container}>
        <data value={chosenTicket.price_jpy} css={styles.priceContainer}>
          <span css={styles.price}>
            ¥{chosenTicket.price_jpy.toLocaleString()}
          </span>{" "}
          <span css={styles.taxLabel}>{t("ui.goodsCard.tax")}</span>
        </data>
        <div css={styles.controlsWrapper}>
          <button
            type="button"
            onClick={handleDecrement}
            disabled={quantity === 0}
            css={[styles.button, styles.decrementButton(quantity === 0)]}
          >
            <div css={styles.iconWrapper}>
              <MinusIcon />
            </div>
          </button>
          <div css={styles.quantityDisplay}>{quantity}</div>
          <button
            type="button"
            onClick={handleIncrement}
            disabled={quantity >= maxPurchase}
            css={[
              styles.button,
              styles.incrementButton(quantity >= maxPurchase),
            ]}
          >
            <div css={styles.iconWrapper}>
              <PlusIcon />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  label: {
    display: "block",
    fontSize: "18px",
    color: "#000",
    marginBottom: "8px",
    fontWeight: "bold",
  },
  container: {
    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
  },
  priceContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  price: {
    fontWeight: "bold",
    fontSize: "20px",
    lineHeight: 1,
  },
  taxLabel: {
    fontSize: "12px",
    lineHeight: 1,
  },
  controlsWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  button: {
    width: "32px",
    height: "32px",
    fontSize: "32px",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  decrementButton: (disabled: boolean) => ({
    borderRight: "none",
    borderRadius: "50% 0 0 50%",
    cursor: disabled ? "not-allowed" : "pointer",
    color: disabled ? "#ccc" : "#333",
  }),
  incrementButton: (disabled: boolean) => ({
    borderLeft: "none",
    borderRadius: "0 50% 50% 0",
    cursor: disabled ? "not-allowed" : "pointer",
    color: disabled ? "#ccc" : "#333",
  }),
  quantityDisplay: {
    height: "32px",
    width: "32px",
    fontWeight: "bold",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderTop: "1px solid #ddd",
    borderBottom: "1px solid #ddd",
    backgroundColor: "#fff",
  },
  iconWrapper: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
} as const;
